import {
  ApolloClient,
  NormalizedCacheObject,
  useApolloClient,
} from '@apollo/client';
import React, { useMemo } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { FCC } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import {
  useAssignedTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';

import { TaskProcessorProvider } from './TaskProcessorProvider';
import { createTaskHandlers } from './handlers';
import {
  LoadManifestFiles,
  RequestFileSync,
} from './handlers/generateNoteHandler';
import {
  getPersistedTasks,
  persistAppointmentTasks,
} from './helpers/persistAppointmentTasks';
import { TaskProcessor } from './task-processor';

export const TaskProcessorProviderWrapper: FCC<{
  /**
   * Reads the durable local manifest for an appointment. Supplied by the app
   * layer (which can reach `@eluve/user-local-files`) so this lib doesn't take a
   * circular dependency on it. [ENG-4717]
   */
  loadManifestFiles?: LoadManifestFiles;
  /**
   * Forces an immediate upload scan when completion reports still-uploading
   * files. Supplied by the app layer (which can reach the file-sync store).
   * [ENG-4717]
   */
  requestFileSync?: RequestFileSync;
}> = ({ children, loadManifestFiles, requestFileSync }) => {
  const apiClient = useApiClient();
  const apolloClient = useApolloClient();
  const logger = useNamedLogger('TaskProcessor');
  const tenantId = useAssignedTenantIdFromParams();
  const userId = useUserIdFromSession();

  const taskProcessor = useMemo(() => {
    const taskHandlers = createTaskHandlers({
      apolloClient: apolloClient as ApolloClient<NormalizedCacheObject>,
      apiClient,
      logger,
      loadManifestFiles,
      requestFileSync,
    });

    return new TaskProcessor({
      userId,
      tenantId,
      dependencies: {
        getPersistedTasks,
        persistAppointmentTasks,
        logger,
        taskHandlers,
      },
    });
  }, [
    userId,
    tenantId,
    apolloClient,
    apiClient,
    logger,
    loadManifestFiles,
    requestFileSync,
  ]);

  return (
    <TaskProcessorProvider taskProcessor={taskProcessor}>
      {children}
    </TaskProcessorProvider>
  );
};
